import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { Toaster } from 'react-hot-toast';

import { useAuth } from './contexts/AuthContext';
import { AppShell } from './components/layout/AppShell';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import ScriptsPage from './pages/ScriptsPage';
import { ScriptsListPage } from './pages/ScriptsListPage';
import { ProjectProvider, useProjectContext } from './contexts/ProjectContext';
import CreateProjectPage from './pages/CreateProjectPage';
import PostmanToJmxPage from './pages/intelliscript/PostmanToJmxPage';

const AIHarToJmeterPage = lazy(() => import('./pages/intelliscript/AIHarToJmeterPage'));
const GetJmxPage = lazy(() => import('./pages/intelliscript/GetJmxPage'));
const CreateCustomScriptPage = lazy(() => import('./pages/intelliscript/CreateCustomScriptPage'));
const SwaggerToJmxPage = lazy(() => import('./pages/intelliscript/SwaggerToJmxPage'));
const SingleScriptPage = lazy(() => import('./pages/intelliload/SingleScriptPage'));
const MultiScriptPage = lazy(() => import('./pages/intelliload/MultiScriptPage'));
const MultiScriptListPage = lazy(() => import('./pages/intelliload/MultiScriptListPage'));
const AutomatedBenchmarkPage = lazy(() => import('./pages/intelliload/AutomatedBenchmarkPage'));
const BenchmarkListPage = lazy(() => import('./pages/intelliload/BenchmarkListPage'));
const BenchmarkCreatePage = lazy(() => import('./pages/intelliload/BenchmarkCreatePage'));
const BenchmarkDetailPage = lazy(() => import('./pages/intelliload/BenchmarkDetailPage'));
const BenchmarkSettingsPage = lazy(() => import('./pages/intelliload/BenchmarkSettingsPage'));
const IntelliReportPage = lazy(() => import('./pages/intellireport/IntelliReportPage'));
const IntelliReportListPage = lazy(() => import('./pages/intellireport/IntelliReportListPage'));

const LlmGatewayOverviewPage = lazy(() => import('./pages/llm-gateway/LlmGatewayOverviewPage'));
const LlmGatewayTenantsPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantsPage'));
const LlmGatewayTenantNewPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantNewPage'));
const LlmGatewayTenantLayout = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantLayout'));
const LlmGatewayTenantSettingsPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantSettingsPage'));
const LlmGatewayTenantIntegrationPage = lazy(
  () => import('./pages/llm-gateway/LlmGatewayTenantIntegrationPage')
);
const LlmGatewayTenantApiKeysPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantApiKeysPage'));
const LlmGatewayTenantProvidersPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantProvidersPage'));
const LlmGatewayTenantModelsPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantModelsPage'));
const LlmGatewayTenantPolicyPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantPolicyPage'));
const LlmGatewayTenantRoutingPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantRoutingPage'));
const LlmGatewayTenantUsagePage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantUsagePage'));
const LlmGatewayTenantLogsPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantLogsPage'));
const LlmGatewayTenantPlaygroundPage = lazy(() => import('./pages/llm-gateway/LlmGatewayTenantPlaygroundPage'));
const LlmGatewayAuditPage = lazy(() => import('./pages/llm-gateway/LlmGatewayAuditPage'));
const UserUsageStatsPage = lazy(() => import('./pages/admin/UserUsageStatsPage'));
const LiveInfrastructurePage = lazy(() => import('./pages/admin/LiveInfrastructurePage'));

function MultiScriptIndexRoute() {
  const location = useLocation();
  const executionId = new URLSearchParams(location.search).get('execution');
  if (executionId) {
    return <Navigate to={`/intelliload/multi-script/execution/${executionId}`} replace />;
  }
  return <MultiScriptListPage />;
}

function App() {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const isResetOrForgotPath = pathname === '/forgot-password' || pathname.startsWith('/reset-password');

  // After login/register, redirect to dashboard when already authenticated
  if (isAuthenticated && (pathname === '/login' || pathname === '/register')) {
    return <Navigate to="/dashboard" replace />;
  }

  // Auth layout: when not authenticated, or when on reset/forgot (so email link works even with stale token)
  if (!isAuthenticated || isResetOrForgotPath) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Toaster position="top-right" />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password/*" element={<ResetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Box>
    );
  }

  // Show authenticated pages with the shell
  return (
    <ProjectProvider>
      <ProjectGate>
        <AppShell>
          <Toaster position="top-right" />
          <Suspense fallback={
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '50vh' 
            }}>
              <CircularProgress />
            </Box>
          }>
            <Routes>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/scripts" element={<ScriptsListPage />} />
              <Route path="/executions" element={<Navigate to="/intelliload/multi-script" replace />} />
              <Route path="/reports" element={<Navigate to="/intellireport" replace />} />
              <Route path="/create-project" element={<CreateProjectPage />} />
              
              {/* IntelliScript */}
              <Route path="/intelliscript/ai-har-to-jmeter" element={<AIHarToJmeterPage />} />
              <Route path="/intelliscript/get-jmx" element={<GetJmxPage />} />
              <Route path="/intelliscript/create-custom" element={<CreateCustomScriptPage />} />
              <Route path="/intelliscript/postman-to-jmx" element={<PostmanToJmxPage />} />
              <Route path="/intelliscript/swagger-to-jmx" element={<SwaggerToJmxPage />} />
              
              {/* IntelliLoad */}
              <Route path="/intelliload/single-script" element={<SingleScriptPage />} />
              <Route path="/intelliload/multi-script/create" element={<MultiScriptPage />} />
              <Route path="/intelliload/multi-script/execution/:executionId" element={<MultiScriptPage />} />
              <Route path="/intelliload/multi-script" element={<MultiScriptIndexRoute />} />
              <Route path="/intelliload/automated-benchmark" element={<AutomatedBenchmarkPage />} />
              
              {/* IntelliLoad Benchmarks (V2) */}
              <Route path="/intelliload/benchmarks" element={<BenchmarkListPage />} />
              <Route path="/intelliload/benchmark/create" element={<BenchmarkCreatePage />} />
              <Route path="/intelliload/benchmark/:benchmarkId/edit" element={<BenchmarkCreatePage />} />
              <Route path="/intelliload/benchmark/:benchmarkId" element={<BenchmarkDetailPage />} />
              <Route path="/intelliload/benchmark-settings" element={<BenchmarkSettingsPage />} />
              
              {/* IntelliReport */}
              <Route path="/intellireport" element={<IntelliReportListPage />} />
              <Route path="/intellireport/generate" element={<IntelliReportPage />} />

              {/* Intelli-LLM (Perfeasy) */}
              <Route path="/llm-gateway/overview" element={<LlmGatewayOverviewPage />} />
              <Route path="/llm-gateway/tenants" element={<LlmGatewayTenantsPage />} />
              <Route path="/llm-gateway/tenants/new" element={<LlmGatewayTenantNewPage />} />
              <Route path="/llm-gateway/audit" element={<LlmGatewayAuditPage />} />
              <Route path="/llm-gateway/tenants/:tenantId" element={<LlmGatewayTenantLayout />}>
                <Route index element={<Navigate to="settings" replace />} />
                <Route path="settings" element={<LlmGatewayTenantSettingsPage />} />
                <Route path="integration" element={<LlmGatewayTenantIntegrationPage />} />
                <Route path="api-keys" element={<LlmGatewayTenantApiKeysPage />} />
                <Route path="providers" element={<LlmGatewayTenantProvidersPage />} />
                <Route path="models" element={<LlmGatewayTenantModelsPage />} />
                <Route path="policy" element={<LlmGatewayTenantPolicyPage />} />
                <Route path="routing" element={<LlmGatewayTenantRoutingPage />} />
                <Route path="usage" element={<LlmGatewayTenantUsagePage />} />
                <Route path="logs" element={<LlmGatewayTenantLogsPage />} />
                <Route path="playground" element={<LlmGatewayTenantPlaygroundPage />} />
              </Route>
              <Route path="/llm-gateway" element={<Navigate to="/llm-gateway/overview" replace />} />

              {/* Super Admin */}
              <Route path="/admin/user-usage" element={<UserUsageStatsPage />} />
              <Route path="/admin/live-infrastructure" element={<LiveInfrastructurePage />} />
              
              {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" />} />
              
              {/* Catch all */}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Suspense>
        </AppShell>
      </ProjectGate>
    </ProjectProvider>
  );
}

// ProjectGate: redirects to /create-project if no projects exist
const ProjectGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { projects, loading } = useProjectContext();
  const isOnCreateProject = window.location.pathname === '/create-project';
  const isLlmGateway = window.location.pathname.startsWith('/llm-gateway');
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  if (!loading && projects.length === 0 && !isOnCreateProject && !isLlmGateway && !isAdminRoute) {
    return <Navigate to="/create-project" replace />;
  }
  return <>{children}</>;
};

export default App; 